import React, { useState, useRef, useEffect } from 'react';
import './AddToCalendarDropdown.css';
import {
  normalizeEventData,
  generateGoogleCalendarUrl,
  generateOutlookLiveUrl,
  triggerNativeIcsDownload
} from '../utils/calendarUtils';

/**
 * Modern SVG Icons for Calendar Providers
 */
const CalendarIcons = {
  google: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" fill="#EA4335"/>
    </svg>
  ),
  outlook: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
      <path d="M21.5 6.5l-8.75-4.2a1.5 1.5 0 0 0-1.5 0L2.5 6.5A1.5 1.5 0 0 0 1.7 7.8v8.4a1.5 1.5 0 0 0 .8 1.3l8.75 4.2a1.5 1.5 0 0 0 1.5 0l8.75-4.2a1.5 1.5 0 0 0 .8-1.3V7.8a1.5 1.5 0 0 0-.8-1.3z" fill="#0078D4"/>
      <path d="M12 3.1l8.5 4.1v7.6L12 18.9 3.5 14.8V7.2L12 3.1z" fill="#28A8EA"/>
      <path d="M6 10h12v4H6z" fill="#FFFFFF"/>
    </svg>
  ),
  apple: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#1e293b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
      <line x1="16" y1="2" x2="16" y2="6"/>
      <line x1="8" y1="2" x2="8" y2="6"/>
      <line x1="3" y1="10" x2="21" y2="10"/>
      <path d="M12 14v4M10 16h4"/>
    </svg>
  ),
  calendarDefault: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
      <line x1="16" y1="2" x2="16" y2="6"/>
      <line x1="8" y1="2" x2="8" y2="6"/>
      <line x1="3" y1="10" x2="21" y2="10"/>
    </svg>
  )
};

/**
 * Reusable AddToCalendarDropdown Component
 * 
 * Props:
 * - course: Course/Event object containing ScheduleID, CourseName, StartDate, EndDate, StartTime, EndTime, etc.
 * - user: Currently authenticated user (to extract trainer details)
 * - onToast: Optional callback function to display toast notifications
 * - variant: 'compact' | 'standard'
 * - label: Optional button label override
 * - align: 'right' (default) | 'left'
 */
const AddToCalendarDropdown = ({
  course,
  user,
  onToast,
  variant = 'compact',
  label = 'Add to Calendar',
  align = 'right'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  if (!course) return null;

  const eventData = normalizeEventData(course, user);

  const handleGoogleClick = (e) => {
    e.preventDefault();
    setIsOpen(false);
    const url = generateGoogleCalendarUrl(eventData);
    window.open(url, '_blank', 'noopener,noreferrer');
    if (onToast) {
      onToast({ type: 'success', msg: `Opening Google Calendar for ${course.CourseName}...` });
    }
  };

  const handleOutlookClick = (e) => {
    e.preventDefault();
    setIsOpen(false);
    const url = generateOutlookLiveUrl(eventData);
    window.open(url, '_blank', 'noopener,noreferrer');
    if (onToast) {
      onToast({ type: 'success', msg: `Opening Outlook Web for ${course.CourseName}...` });
    }
  };

  const handleNativeIcsClick = async (e) => {
    e.preventDefault();
    setIsOpen(false);
    await triggerNativeIcsDownload(eventData, onToast);
  };

  return (
    <div className={`atc-dropdown-container ${align === 'left' ? 'align-left' : ''}`} ref={dropdownRef}>
      <button
        type="button"
        className={`atc-trigger-btn ${variant === 'compact' ? 'compact' : ''} ${isOpen ? 'active' : ''}`}
        onClick={() => setIsOpen(prev => !prev)}
        title="Add to Google Calendar, Outlook, or Apple/Device Calendar"
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        {CalendarIcons.calendarDefault}
        <span>{label}</span>
        <span className="atc-caret">▾</span>
      </button>

      {isOpen && (
        <div className="atc-menu" role="menu">
          <div className="atc-menu-header">Add to Calendar</div>

          {/* 1. Google Calendar */}
          <button
            type="button"
            className="atc-item"
            role="menuitem"
            onClick={handleGoogleClick}
          >
            <div className="atc-icon-badge badge-google">
              {CalendarIcons.google}
            </div>
            <div className="atc-text-wrap">
              <span className="atc-title">Google Calendar</span>
              <span className="atc-desc">Web browser prompt</span>
            </div>
          </button>

          {/* 2. Outlook Web / Office 365 */}
          <button
            type="button"
            className="atc-item"
            role="menuitem"
            onClick={handleOutlookClick}
          >
            <div className="atc-icon-badge badge-outlook">
              {CalendarIcons.outlook}
            </div>
            <div className="atc-text-wrap">
              <span className="atc-title">Outlook Web</span>
              <span className="atc-desc">Microsoft 365 / Live</span>
            </div>
          </button>

          {/* 3. Apple & Native Calendar (.ics Hand-Off) */}
          <button
            type="button"
            className="atc-item"
            role="menuitem"
            onClick={handleNativeIcsClick}
          >
            <div className="atc-icon-badge badge-apple">
              {CalendarIcons.apple}
            </div>
            <div className="atc-text-wrap">
              <span className="atc-title">Apple / System iCal</span>
              <span className="atc-desc">Native .ics download</span>
            </div>
          </button>
        </div>
      )}
    </div>
  );
};

export default AddToCalendarDropdown;
